import { Injectable } from '@angular/core';
import {
  EMPTY,
  Observable,
  TimeoutError,
  catchError,
  concat,
  concatMap,
  defer,
  from,
  mergeMap,
  of,
  timeout
} from 'rxjs';
import * as XLSX from 'xlsx';

import {
  BulkDeleteHistoryResponse,
  ScanPullImportReport
} from '../Models/ApplicationModels';
import { MasterDataSeriveService } from './master-data-serive.service';

export interface ParsedBarcodeFile {
  rows: number;
  barcodes: string[];
}

export interface ScanPullImportProgressEvent {
  type: 'progress';
  batch: number;
  totalBatches: number;
  text: string;
}

export interface ScanPullImportReportEvent {
  type: 'report';
  report: ScanPullImportReport;
  /** Barcodes this run actually scan pulled, for callers that trim their grid. */
  pulledBarcodes: string[];
}

export type ScanPullImportEvent =
  | ScanPullImportProgressEvent
  | ScanPullImportReportEvent;

/**
 * Drives Scan Pull by import: reads a CSV or Excel barcode file and scan pulls
 * every barcode in it through the standard scan pull endpoint.
 *
 * Shared by the Scan Pull and Barcode Management screens so both behave
 * identically.
 */
@Injectable({
  providedIn: 'root'
})
export class ScanPullImportService {
  /**
   * Batch size for the dispatch. The API fans IMS deletes out in parallel groups
   * of 50 inside one transaction, so batching bounds the transaction and the IMS
   * fan-out, keeps each request inside the client timeout, and makes a mid-import
   * failure partial and precisely accounted for rather than total.
   */
  private static readonly CHUNK_SIZE = 200;

  /** Per-request budget, matching the manual scan pull. */
  private static readonly REQUEST_TIMEOUT_MS = 270_000;

  static readonly MAX_ROWS = 10000;
  static readonly ACCEPT_ATTRIBUTE = '.csv, .xlsx, .xls';

  constructor(private masterservice: MasterDataSeriveService) {}

  isSupportedFile(fileName: string): boolean {
    return /\.(csv|xlsx|xls)$/i.test(fileName ?? '');
  }

  /** Reads and parses the file, choosing the parser from its extension. */
  readBarcodeFile(file: File): Observable<ParsedBarcodeFile> {
    return new Observable<ParsedBarcodeFile>((subscriber) => {
      const isCsv = /\.csv$/i.test(file.name);
      const reader = new FileReader();

      reader.onerror = () =>
        subscriber.error(new Error('Unable to read the selected file.'));

      reader.onload = () => {
        try {
          subscriber.next(
            isCsv
              ? ScanPullImportService.parseBarcodeCsv(String(reader.result ?? ''))
              : ScanPullImportService.parseBarcodeWorkbook(
                  reader.result as ArrayBuffer
                )
          );
          subscriber.complete();
        } catch {
          // A corrupt or password-protected workbook makes XLSX.read throw.
          subscriber.error(
            new Error(
              'Unable to read the selected file. Please check the file and try again.'
            )
          );
        }
      };

      if (isCsv) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  }

  /**
   * Scan pulls the barcodes in sequential batches, emitting progress as batches
   * complete and a final report once the run finishes or stops on an error.
   */
  importBarcodes(
    fileName: string,
    rows: number,
    barcodes: string[]
  ): Observable<ScanPullImportEvent> {
    const chunks = ScanPullImportService.chunk(
      barcodes,
      ScanPullImportService.CHUNK_SIZE
    );

    const report: ScanPullImportReport = {
      fileName,
      rowsInFile: rows,
      uniqueBarcodes: barcodes.length,
      attemptedCount: 0,
      scanPulledCount: 0,
      failedCount: 0,
      failures: [],
      stoppedEarly: false
    };

    // Only barcodes from batches that actually completed — a run that stops early
    // must not report or clear barcodes it never processed.
    const attemptedBarcodes: string[] = [];

    return concat(
      of(ScanPullImportService.progressEvent(1, chunks.length)),

      from(chunks).pipe(
        concatMap((chunk, index) =>
          this.masterservice.scanpullBarcodes(chunk).pipe(
            timeout(ScanPullImportService.REQUEST_TIMEOUT_MS),
            mergeMap((res) => {
              const payload = res as BulkDeleteHistoryResponse;

              // chunk.length stands in for payload.totalCount: the barcodes were
              // de-duplicated before dispatch, so the server's Distinct() is a no-op.
              report.attemptedCount += chunk.length;
              attemptedBarcodes.push(...chunk);
              report.failures.push(...(payload?.failedToDeleteSerials ?? []));

              return index + 1 < chunks.length
                ? of(
                    ScanPullImportService.progressEvent(index + 2, chunks.length)
                  )
                : EMPTY;
            })
          )
        ),
        catchError((err) => {
          report.stoppedEarly = true;
          report.errorMessage = ScanPullImportService.describeError(err);
          return EMPTY;
        })
      ),

      defer(() => of(ScanPullImportService.finish(report, attemptedBarcodes)))
    );
  }

  /**
   * Parses a CSV import file: one TPM barcode per row.
   * Read as plain text so digit-only barcodes keep their exact characters.
   */
  static parseBarcodeCsv(text: string): ParsedBarcodeFile {
    const rows = text
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .map((line) => line.split(',').map((field) => field.trim()));

    return ScanPullImportService.normalizeBarcodeRows(rows);
  }

  /**
   * Parses an Excel import file (.xlsx / .xls): one TPM barcode per row of the
   * first worksheet.
   */
  static parseBarcodeWorkbook(data: ArrayBuffer): ParsedBarcodeFile {
    const workbook = XLSX.read(new Uint8Array(data), { type: 'array' });
    const sheetName = workbook.SheetNames[0];

    if (!sheetName) {
      return { rows: 0, barcodes: [] };
    }

    const grid = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      blankrows: false,
      defval: ''
    }) as any[][];

    const rows = grid.map((row) =>
      (row ?? []).map((cell) => ScanPullImportService.cellToBarcode(cell))
    );

    return ScanPullImportService.normalizeBarcodeRows(rows);
  }

  /**
   * Excel stores a digit-only barcode as a number. Rendering it through the
   * cell's display format risks scientific notation, so integers are written out
   * digit for digit instead.
   */
  private static cellToBarcode(cell: any): string {
    if (cell === null || cell === undefined) {
      return '';
    }
    if (typeof cell === 'number') {
      return Number.isInteger(cell) ? cell.toFixed(0) : String(cell);
    }
    return String(cell).trim();
  }

  /**
   * Shared normalisation for both file types: take the first populated cell of
   * each row, drop empty rows, skip an optional "Barcode" column title, and
   * de-duplicate case-insensitively while preserving file order.
   */
  private static normalizeBarcodeRows(rows: string[][]): ParsedBarcodeFile {
    const values = rows
      .map(
        (row) =>
          (row ?? [])
            .map((field) =>
              (field ?? '').trim().replace(/^"(.*)"$/, '$1').trim()
            )
            .find((field) => field.length > 0) ?? ''
      )
      .filter((value) => value.length > 0);

    // The column title is optional — stripped when present, and every row is
    // treated as a barcode when it is absent.
    const body =
      values.length > 0 && /^barcode$/i.test(values[0])
        ? values.slice(1)
        : values;

    const seen = new Set<string>();
    const barcodes = body.filter((barcode) => {
      const key = barcode.toUpperCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    return { rows: body.length, barcodes };
  }

  private static chunk(barcodes: string[], size: number): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < barcodes.length; i += size) {
      chunks.push(barcodes.slice(i, i + size));
    }
    return chunks;
  }

  private static progressEvent(
    batch: number,
    totalBatches: number
  ): ScanPullImportProgressEvent {
    const safeBatch = Math.min(batch, totalBatches);

    return {
      type: 'progress',
      batch: safeBatch,
      totalBatches,
      text:
        totalBatches <= 1
          ? 'Processing import...'
          : `Processing batch ${safeBatch} of ${totalBatches}...`
    };
  }

  private static finish(
    report: ScanPullImportReport,
    attemptedBarcodes: string[]
  ): ScanPullImportReportEvent {
    report.failedCount = report.failures.length;
    report.scanPulledCount = report.attemptedCount - report.failedCount;

    const failedBarcodes = new Set(
      report.failures.map((failure) =>
        (failure.serialNumber ?? '').toUpperCase()
      )
    );

    return {
      type: 'report',
      report,
      pulledBarcodes: attemptedBarcodes.filter(
        (barcode) => !failedBarcodes.has(barcode.toUpperCase())
      )
    };
  }

  private static describeError(err: any): string {
    if (err instanceof TimeoutError) {
      return (
        'The scan pull request timed out. Barcodes in the current batch may ' +
        'already be processed — please verify before re-importing.'
      );
    }
    if (err?.error?.errors?.length > 0) {
      return err.error.errors[0].message;
    }
    if (err?.error?.detail) {
      return err.error.detail;
    }
    if (err?.message) {
      return err.message;
    }
    return 'Error occurred';
  }
}
